import PropTypes from "prop-types";
import { createContext, useEffect, useReducer, useCallback } from "react";
import { setSession } from "./utils";
import { Hub } from "aws-amplify/utils";
import * as Auth from "aws-amplify/auth";

// ----------------------------------------------------------------------

// NOTE:
// We only build demo at basic level.
// Customer will need to do some extra handling yourself if you want to extend the logic and other features...

// ----------------------------------------------------------------------

const initialState = {
  isInitialized: false,
  isAuthenticated: false,
  user: null,
};

const reducer = (state, action) => {
  if (action.type === "INITIAL") {
    return {
      isInitialized: true,
      isAuthenticated: action.payload.isAuthenticated,
      user: action.payload.user,
    };
  }
  if (action.type === "LOGIN") {
    return {
      ...state,
      isAuthenticated: true,
      user: action.payload.user,
    };
  }
  if (action.type === "LOGOUT") {
    return {
      ...state,
      isAuthenticated: false,
      user: null,
    };
  }

  return state;
};

// ----------------------------------------------------------------------

export const AuthContext = createContext(null);

// ----------------------------------------------------------------------

AuthProvider.propTypes = {
  children: PropTypes.node,
};

const withRole = (user) => {
  const groups = user["cognito:groups"];
  if (groups.includes("admin")) {
    return { ...user, role: "admin" };
  } else if (groups.includes("super-support")) {
    return { ...user, role: "super-support" };
  } else if (groups.includes("support")) {
    return { ...user, role: "support" };
  }
  return user;
};

const withSessionInfo = async (user) => {
  const session = await Auth.fetchAuthSession();
  return withRole({
    ...user,
    ...session?.tokens?.idToken?.payload,
  });
};

export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const initialize = useCallback(async () => {
    try {
      const user = await Auth.getCurrentUser();
      if (user) {
        dispatch({
          type: "INITIAL",
          payload: {
            isAuthenticated: true,
            user: await withSessionInfo(user),
          },
        });
      } else {
        dispatch({
          type: "INITIAL",
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      }
    } catch (error) {
      console.error(error);
      dispatch({
        type: "INITIAL",
        payload: {
          isAuthenticated: false,
          user: null,
        },
      });
    }
  }, []);

  useEffect(() => {
    initialize();

    const authUnsubscribe = Hub.listen("auth", ({ payload: { event } }) => {
      switch (event) {
        case "signedIn":
          initialize();
        case "signOut":
          dispatch({
            type: "LOGOUT",
            payload: {
              isAuthenticated: false,
              user: null,
            },
          });
          break;
        default:
          break;
      }
    });

    return () => {
      authUnsubscribe();
    };
  }, [initialize]);

  const login = async (email, password) => {
    const response = await Auth.signIn(email, password);
    const user = await Auth.getCurrentUser();
    dispatch({
      type: "LOGIN",
      payload: {
        user: await withSessionInfo(user),
      },
    });
    return response;
  };

  // LOGOUT
  const logout = async () => {
    setSession(null);
    await Auth.signOut();
    dispatch({
      type: "LOGOUT",
    });
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: "amplify",
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
